import { NavLink, useLocation } from 'react-router';
import type { LucideIcon } from 'lucide-react';

export interface AdminMobileNavItem {
  icon: LucideIcon;
  label: string;
  href: string;
}

interface AdminMobileNavProps {
  /** Flat list of admin nav items, in display order. */
  items: AdminMobileNavItem[];
}

/**
 * Sticky bottom navigation for admin routes on mobile (`md:hidden`).
 *
 * Renders all admin links in a single horizontally scrollable strip — TikTok /
 * Instagram explore tabs style. Desktop continues to use the lateral sidebar
 * defined in `AdminLayout`.
 */
export default function AdminMobileNav({ items }: AdminMobileNavProps) {
  const { pathname } = useLocation();

  const isActive = (href: string) => {
    if (href === '/admin') return pathname === '/admin';
    return pathname.startsWith(href);
  };

  return (
    <nav
      aria-label="Admin primary navigation"
      className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-bg-secondary/95 backdrop-blur-lg border-t border-border-subtle"
      style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
    >
      <div
        className="
          flex items-stretch gap-1 overflow-x-auto scroll-smooth px-2
          [scrollbar-width:none] [-ms-overflow-style:none]
          [&::-webkit-scrollbar]:hidden
        "
      >
        {items.map((item) => {
          const Icon = item.icon;
          const active = isActive(item.href);
          return (
            <NavLink
              key={item.href}
              to={item.href}
              end={item.href === '/admin'}
              aria-current={active ? 'page' : undefined}
              aria-label={item.label}
              className={`
                relative shrink-0 flex flex-col items-center justify-center gap-0.5
                px-3 py-2 min-w-[64px] min-h-[56px] rounded-md
                text-[11px] font-medium transition-colors
                focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent-primary
                ${
                  active
                    ? 'text-accent-primary'
                    : 'text-text-secondary hover:text-text-primary'
                }
              `}
            >
              <Icon className="w-5 h-5" aria-hidden="true" />
              <span className="leading-tight whitespace-nowrap">
                {item.label}
              </span>
              {active && (
                <span
                  aria-hidden="true"
                  className="absolute top-0 left-1/2 -translate-x-1/2 w-8 h-0.5 rounded-b-full bg-accent-primary"
                />
              )}
            </NavLink>
          );
        })}
      </div>
    </nav>
  );
}
